import { z } from 'zod';
import { Clock, CounterClock, STATIC_CLOCK } from './clock';
import { AnyDelta, DeltaBase } from './deltas';

export type AnyDomainConfig = {
  /** Mutable, but without common properties like `state` or `oid` */
  mutableBase: Omit<any, 'state' | 'oid' | 'mutable'>;
  state: any;
  delta: Omit<any, keyof DeltaBase> & DeltaBase;
  /** Defaults to returning the mutable */
  getter?: { returns: any };
};

export type MutableOf<C extends AnyDomainConfig> = C['mutableBase'] & {
  /** Every mutable has an object ID, which it uses to find its deltas */
  readonly oid: string;
  /**
   * The oid of all objects, including this one, which make up the mutable's
   * current state.
   **/
  readonly visibleOids: readonly string[];

  readonly mutable: true;
  readonly state: C['state'];

  /**
   * If the delta provided would change this mutable's state.
   *
   * Assumes that delta.oid === this.oid.
   **/
  wouldChange(delta: AnyDelta): boolean;
};
export type StateOf<C extends AnyDomainConfig> = C['state'];
export type DeltaOf<C extends AnyDomainConfig> = C['delta'];
export type GetterReturnOf<C extends AnyDomainConfig> = C['getter'] extends {
  returns: infer R;
}
  ? R
  : MutableOf<C>;
export type ConfigOf<C extends Domain<any>> = C extends Domain<infer R>
  ? R
  : never;

/**
 * A Domain represents a data type in the CRDT system. All data types such as
 * Records or Lists implement this interface.
 * */
export interface Domain<C extends AnyDomainConfig> {
  /** Creates a instance of the Domain and returns it.  */
  create(oid: string, initialState: C['state'], cxn: Connection): MutableOf<C>;

  /**
   * A getter, when used in a container domain (e.g. list, record).
   */
  getter(m: MutableOf<C>): GetterReturnOf<C>;
  /**
   * A setter.  When not provided, container domains will not allow
   * this to be set (e.g. no record.item = ...); users will need to
   * interact via `v`s api directly to cause mutations.
   **/
  setter?(m: MutableOf<C>, v: GetterReturnOf<C>): void;

  statesEqual(a: StateOf<C>, b: StateOf<C>): boolean;
  stateSchema: z.ZodType<StateOf<C>, z.ZodTypeDef, unknown>;

  isMutable(m: unknown): m is MutableOf<C>;
}

export interface Connection {
  /** Clock used for the delta.time fields. */
  readonly clock: Clock;
  /**
   * Called everytime a new instance (aka mutable) of a domain is
   * constructed. It provides the `id` of the new instance and a function that
   * can be used to send deltas to the new instance. This is used primarily to
   * apply changes from the server. Local changes apply directly to the mutable
   * outside of this code flow.
   */
  register(id: MutableOf<any>, toMutable: (delta: AnyDelta) => void): void;
  /**
   * Called whenever a change is applied to an instance, including
   * via the `toMutable` function passed to `register`. Used to notify the
   * server of local changes that could have originated from multiple places in
   * the UI.
   */
  fromMutable(...deltas: AnyDelta[]): void;
}

/** Rejects all deltas */
export const NOOP_CONNECTION: Connection = {
  clock: STATIC_CLOCK,
  register: () => {},
  fromMutable: () => {
    throw new Error('NOOP_CONNECTION cannot receive deltas');
  },
};

/** Applies all deltas immediately; no undo/redo */
export class SimpleConnection implements Connection {
  readonly clock = new CounterClock();

  readonly toMutable = new Map<string, (delta: AnyDelta) => void>();

  register(id: MutableOf<any>, toMutable: (delta: AnyDelta) => void) {
    if (this.toMutable.has(id.oid)) {
      throw new Error(`oid ${id.oid} already registered`);
    }
    this.toMutable.set(id.oid, toMutable);
  }

  fromMutable(...deltas: AnyDelta[]) {
    for (const d of deltas) {
      const cb = this.toMutable.get(d.for);
      if (!cb) {
        throw new Error(`got delta for unregistered oid: ${d.for}`);
      }
      cb(d);
    }
  }
}
